import React, { FC, useMemo, useState } from 'react'
import {
  Button,
  ButtonGroup,
  elFlexGrow,
  elHFull,
  elPt0,
  elPx7,
  elPy5,
  elTextCenter,
  elWFull,
  FlexContainer,
  Nav,
  NavItem,
  TextBase,
  useMediaQuery,
} from '@reapit/elements'
import { useParams, useNavigate } from 'react-router-dom'
import { cx } from '@linaria/core'
import { TENANT_FORM_ROUTES } from '../../routes'
import { elNav, elBorderNone } from './style'
import { useFormDocument } from '../layout/tenant-app-layout'
import { DialogSubmitAgreement, useDialogSubmitAgreement } from '@reapitform/common-ui'
import { DialogHelp } from '../../dialog-help-agreement'
export interface ISteps {
  nextStep?: number
  lastStep?: number
  previousStep?: number
  currentStep?: number
}
interface IStepNavigation {
  children?: React.JSX.Element | React.ReactNode | string | undefined
  steps?: ISteps
  title?: string
  nextButtonText?: string
  hideTitleOnMobile?: boolean
  hideNav?: boolean
  hasDisabledNextButton?: boolean
}

const StepNavigation: FC<IStepNavigation> = ({
  children,
  steps,
  title = '',
  nextButtonText,
  hideTitleOnMobile,
  hideNav,
  hasDisabledNextButton,
}) => {
  const navigate = useNavigate()
  const { isMobile } = useMediaQuery()
  const { recipientId, stepsId } = useParams()
  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState<boolean>(false)
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false)

  const formDocumentData = useFormDocument()
  const isChangeRequested = useMemo(
    () => Boolean(formDocumentData?.changeRequests.length),
    [formDocumentData?.changeRequests.length],
  )

  const isOnTheStep: boolean = stepsId ? true : false
  const isTheFirstStep: boolean = Number(steps?.currentStep) === 1

  const { handleSubmitAgreement, isSubmitLoading } = useDialogSubmitAgreement(isChangeRequested)

  const handleChangeStep = (stepsId: any) => {
    navigate(`${TENANT_FORM_ROUTES.TENANT_BASED}/${recipientId}/steps/${stepsId}`)
  }

  const submitAgreement = () => {
    if (isSubmitLoading) return
    if (isChangeRequested) return setIsSubmittingAgreement(true)
    handleSubmitAgreement()
  }

  const onNext = () => {
    if (!steps) return

    if (!steps.nextStep) return submitAgreement()
    handleChangeStep(steps.nextStep)
  }

  const onBack = () => {
    if (isTheFirstStep) return
    handleChangeStep(steps?.previousStep)
  }

  function navigateToHelpPage() {
    setIsHelpOpen(true)
  }

  return (
    <FlexContainer isFlexColumn className={cx(elHFull, elPt0)}>
      <Nav className={cx(hideNav ? cx(elNav, elBorderNone) : '')}>
        {!isMobile && (
          <>
            <NavItem>
              <TextBase hasBoldText className={elFlexGrow}>
                {title || formDocumentData?.formDisplayName}
              </TextBase>
            </NavItem>
            {isOnTheStep && (
              <ButtonGroup className={elPx7}>
                {!isTheFirstStep && (
                  <Button onClick={onBack} buttonIcon={{ icon: 'arrowLeft', position: 'left' }}>
                    Back
                  </Button>
                )}
                <Button
                  onClick={onNext}
                  intent="primary"
                  buttonIcon={{ icon: 'arrowRight', position: 'right' }}
                  disabled={hasDisabledNextButton}
                >
                  {nextButtonText}
                </Button>
              </ButtonGroup>
            )}
          </>
        )}
        {isMobile && isOnTheStep && (
          <NavItem>
            <ButtonGroup>
              {!isTheFirstStep && (
                <Button onClick={onBack} style={{ border: 0 }}>
                  <TextBase intent="primary">Back</TextBase>
                </Button>
              )}
            </ButtonGroup>
            <TextBase hasBoldText className={cx(elFlexGrow, elTextCenter)}>
              {!hideTitleOnMobile && (title || formDocumentData?.formDisplayName)}
            </TextBase>
            <ButtonGroup>
              <Button style={{ border: 0 }} onClick={navigateToHelpPage}>
                <TextBase intent="low">Help</TextBase>
              </Button>
            </ButtonGroup>
          </NavItem>
        )}
      </Nav>
      <>{children}</>

      {isMobile && isOnTheStep && (
        <FlexContainer className={cx(elPy5, elPx7)}>
          <Button
            onClick={onNext}
            className={elWFull}
            intent="primary"
            buttonIcon={{ icon: 'arrowRight', position: 'right' }}
            buttonSize="large"
            disabled={hasDisabledNextButton}
          >
            {nextButtonText}
          </Button>
        </FlexContainer>
      )}

      <DialogSubmitAgreement
        handleSubmitAgreement={handleSubmitAgreement}
        isLoading={isSubmitLoading}
        handlerModalClose={() => setIsSubmittingAgreement(false)}
        isModalOpen={isSubmittingAgreement}
        changeRequested={formDocumentData?.changeRequests}
      />

      {isMobile && isOnTheStep && (
        <DialogHelp isModalOpen={isHelpOpen} handlerModalClose={() => setIsHelpOpen(false)} />
      )}
    </FlexContainer>
  )
}

export default StepNavigation
